import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { syncFromDatadog, useOverview } from './useReleaseCadence';
import { formatDate, formatNumber } from './format';
import ConfidenceView from './ConfidenceView';
import CadenceView from './CadenceView';
import WavesView from './WavesView';
import PlanVsActualView from './PlanVsActualView';
import ReadinessView from './ReadinessView';
import HealthView from './HealthView';
import CommitmentView from './CommitmentView';

const PLAN_TAB = 6;

const HistoryLine =({ overview, syncing, onSync, syncError }) => {
  const { history, lastSync } = overview;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ color: MUTED }}>
        {history.runs
          ? `${formatNumber(history.runs)} deployment runs recorded, ${formatDate(history.earliest)} to ${formatDate(history.latest)}. History is kept in the portal because Datadog only retains about 30 days.`
          : 'No deployment runs recorded yet.'}
      </Typography>
      <Button size="small" variant="outlined" onClick={onSync} disabled={syncing} startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />} sx={{ textTransform: 'none', borderRadius: '10px' }}>
        Sync from Datadog
      </Button>
      {syncError && <Typography variant="body2" color="error">{syncError}</Typography>}
      {!syncError && lastSync && !lastSync.error && lastSync.added != null && (
        <Typography variant="caption" sx={{ color: MUTED }}>Last sync added {formatNumber(lastSync.added)} new runs.</Typography>
      )}
    </Box>
  );
};

const ReleaseCadenceTab = () => {
  const { data: overview, loading, error, refreshError, reload } = useOverview();
  const [tab, setTab] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');

  const openPlans = () => setTab(PLAN_TAB);

  const handleSync = async () => {
    setSyncing(true);
    setSyncError('');
    try {
      await syncFromDatadog();
      await reload();
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !overview) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!overview) return null;

  return (
    <>
      {refreshError && <Alert severity="warning" sx={{ mb: 2 }}>Could not refresh: {refreshError}. Showing the last data loaded.</Alert>}
      {tab !== 0 && <HistoryLine overview={overview} syncing={syncing} onSync={handleSync} syncError={syncError} />}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mt: 1, mb: 2.5, borderBottom: '1px solid rgba(0,0,0,0.08)', '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.9375rem' }, '& .Mui-selected': { color: DEEP_GREEN }, '& .MuiTabs-indicator': { backgroundColor: DEEP_GREEN } }}
      >
        <Tab label="Confidence" />
        <Tab label="Cadence" />
        <Tab label="Rollout waves" />
        <Tab label="Readiness" />
        <Tab label="Health" />
        <Tab label="Commitment" />
        <Tab label="Plan vs actual" />
      </Tabs>
      {tab === 0 && <ConfidenceView onOpenPlans={openPlans} />}
      {tab === 1 && <CadenceView overview={overview} />}
      {tab === 2 && <WavesView overview={overview} onOpenPlans={openPlans} />}
      {tab === 3 && <ReadinessView overview={overview} onOpenPlans={openPlans} />}
      {tab === 4 && <HealthView overview={overview} onOpenPlans={openPlans} />}
      {tab === 5 && <CommitmentView />}
      {tab === PLAN_TAB && <PlanVsActualView overview={overview} onChanged={reload} />}
    </>
  );
};

export default ReleaseCadenceTab;
